<template>
    <div
        v-if="isVisible"
        ref="trayRoot"
        class="vt-toast-history-root"
        data-test="toast-history-root"
    >
        <!-- Floating Popover Panel -->
        <transition name="vt-toast-tray-fade">
            <div
                v-if="isOpen"
                class="vt-toast-history-panel shadow-lg"
                data-test="panel-toast-history"
                role="dialog"
                aria-label="Recent toast notifications"
            >
                <div class="vt-toast-history-header d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center min-w-0">
                        <i class="bi bi-clock-history mr-2 text-info"></i>
                        <div class="min-w-0">
                            <div class="vt-toast-history-title font-weight-bold text-truncate">
                                Recent Notifications
                                <span v-if="items.length > 0" class="vt-toast-history-total-pill ml-1">{{ items.length }}</span>
                            </div>
                            <div class="vt-toast-history-subtitle text-muted">
                                Kept for 3 days
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center flex-shrink-0">
                        <button
                            v-if="items.length > 0"
                            type="button"
                            class="btn btn-xs btn-link text-muted p-0 mr-2 vt-toast-clear-btn"
                            data-test="btn-toast-history-clear"
                            title="Clear notification history"
                            aria-label="Clear notification history"
                            @click.stop="clearHistory"
                        >
                            <i class="bi bi-trash3 mr-1"></i>Clear
                        </button>
                        <button
                            type="button"
                            class="btn btn-xs btn-link text-muted p-0 vt-toast-close-btn"
                            data-test="btn-toast-history-close"
                            title="Close (Esc)"
                            aria-label="Close notification history"
                            @click.stop="closePanel"
                        >
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <!-- Scrollable Notification List -->
                <div
                    ref="historyScrollList"
                    class="vt-toast-history-list"
                    data-test="list-toast-history"
                >
                    <div v-if="items.length === 0" class="vt-toast-history-empty text-center py-4 px-3" data-test="empty-toast-history">
                        <i class="bi bi-bell-slash d-block mb-1 text-muted" style="font-size: 1.35rem;"></i>
                        <div class="font-weight-bold small">No recent notifications</div>
                        <div class="extra-small text-muted mt-1">
                            Toast alerts from the last 3 days will appear here so you never miss a message.
                        </div>
                    </div>

                    <div
                        v-for="item in visibleItems"
                        :key="item.id"
                        :ref="'historyItem-' + item.id"
                        class="vt-toast-history-item"
                        :class="[`vt-toast-item-${item.variant || 'info'}`]"
                        data-test="item-toast-history"
                        :data-variant="item.variant || 'info'"
                    >
                        <div class="vt-toast-item-icon-col">
                            <i :class="variantIcon(item.variant)"></i>
                        </div>
                        <div class="vt-toast-item-content min-w-0 flex-grow-1">
                            <div class="d-flex align-items-start justify-content-between">
                                <span class="vt-toast-item-title font-weight-bold">
                                    {{ item.title }}
                                    <span
                                        v-if="item.repeatCount > 1"
                                        class="vt-toast-repeat-badge ml-1"
                                        :title="`Repeated ${item.repeatCount} times`"
                                    >&times;{{ item.repeatCount }}</span>
                                </span>
                                <span
                                    class="vt-toast-item-time text-muted ml-2 flex-shrink-0"
                                    :title="formatFullTime(item.timestamp)"
                                >
                                    {{ formatRelativeTime(item.timestamp) }}
                                </span>
                            </div>
                            <div
                                v-if="item.message && item.message !== item.title"
                                class="vt-toast-item-msg mt-1"
                            >
                                {{ item.message }}
                            </div>
                        </div>
                    </div>

                    <!-- Load More Button -->
                    <div v-if="remainingCount > 0" class="vt-toast-load-more-wrap pt-1 pb-1 text-center">
                        <button
                            type="button"
                            class="btn btn-xs btn-outline-secondary vt-toast-load-more-btn w-100"
                            data-test="btn-toast-history-load-more"
                            @click.stop="loadMore"
                        >
                            <i class="bi bi-chevron-down mr-1"></i>
                            Load more ({{ Math.min(pageSize, remainingCount) }} of {{ remainingCount }} older)
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Bottom-Right Floating Trigger Button -->
        <button
            type="button"
            class="vt-toast-history-trigger shadow-sm"
            :class="{
                'is-open': isOpen,
                'has-unread': unreadCount > 0,
                [`latest-${latestVariant}`]: items.length > 0
            }"
            data-test="btn-toast-history"
            aria-label="Recent toast notifications"
            :title="triggerTooltip"
            @click.stop="togglePanel"
        >
            <span class="vt-toast-trigger-dot" v-if="items.length > 0" :class="`dot-${latestVariant}`"></span>
            <i class="bi bi-clock-history vt-toast-trigger-icon"></i>
            <span v-if="items.length > 0" class="vt-toast-trigger-count" data-test="badge-toast-history-count">
                {{ items.length > 99 ? '99+' : items.length }}
            </span>
        </button>
    </div>
</template>

<script>
import eventBus from '../event-bus';
import {
    loadToastHistory,
    clearToastHistoryStorage,
    markAllToastHistoryRead
} from '../mixinToast';

export default {
    name: 'toast-history-tray',
    props: {
        isVisible: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isOpen: false,
            items: loadToastHistory(),
            pageSize: 5,
            visibleCount: 5,
            nowTick: Date.now()
        };
    },
    computed: {
        visibleItems() {
            return (this.items || []).slice(0, this.visibleCount);
        },
        remainingCount() {
            return Math.max(0, (this.items || []).length - this.visibleCount);
        },
        unreadCount() {
            return (this.items || []).filter(i => i && i.unread).length;
        },
        latestVariant() {
            const first = (this.items || [])[0];
            return (first && first.variant) ? first.variant : 'info';
        },
        triggerTooltip() {
            if (!this.items.length) return 'Recent notifications (last 3 days)';
            const first = this.items[0];
            return `Recent notifications (${this.items.length}) — Latest: ${first.title}`;
        }
    },
    mounted() {
        this._onHistoryUpdated = (updatedList) => {
            this.items = Array.isArray(updatedList) ? [...updatedList] : loadToastHistory();
            if (this.isOpen) {
                markAllToastHistoryRead();
            }
        };
        eventBus.$on('vt-toast-history-updated', this._onHistoryUpdated);

        this._onDocMouseDown = (e) => {
            if (!this.isOpen) return;
            const root = this.$refs.trayRoot;
            if (root && root.contains(e.target)) return;
            this.closePanel();
        };
        this._onDocKeyDown = (e) => {
            if (this.isOpen && (e.key === 'Escape' || e.key === 'Esc')) {
                this.closePanel();
            }
        };
        document.addEventListener('mousedown', this._onDocMouseDown, true);
        document.addEventListener('keydown', this._onDocKeyDown);

        this._tickTimer = setInterval(() => {
            this.nowTick = Date.now();
        }, 30000);
    },
    beforeUnmount() {
        if (this._onHistoryUpdated) {
            eventBus.$off('vt-toast-history-updated', this._onHistoryUpdated);
        }
        if (this._onDocMouseDown) {
            document.removeEventListener('mousedown', this._onDocMouseDown, true);
        }
        if (this._onDocKeyDown) {
            document.removeEventListener('keydown', this._onDocKeyDown);
        }
        if (this._tickTimer) {
            clearInterval(this._tickTimer);
        }
    },
    methods: {
        togglePanel() {
            if (this.isOpen) {
                this.closePanel();
            } else {
                this.openPanel();
            }
        },
        openPanel() {
            this.items = loadToastHistory();
            this.visibleCount = this.pageSize;
            this.nowTick = Date.now();
            this.isOpen = true;
            markAllToastHistoryRead();
            this.items = loadToastHistory();
        },
        closePanel() {
            this.isOpen = false;
        },
        loadMore() {
            const firstNewIndex = this.visibleCount;
            this.visibleCount = Math.min(this.items.length, this.visibleCount + this.pageSize);
            this.$nextTick(() => {
                const targetItem = this.items[firstNewIndex];
                if (targetItem) {
                    const refVal = this.$refs['historyItem-' + targetItem.id];
                    const itemEl = Array.isArray(refVal) ? refVal[0] : refVal;
                    if (itemEl && typeof itemEl.scrollIntoView === 'function') {
                        itemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        return;
                    }
                }
                const listEl = this.$refs.historyScrollList;
                if (listEl) {
                    listEl.scrollTo({
                        top: listEl.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            });
        },
        clearHistory() {
            clearToastHistoryStorage();
            this.items = [];
            this.visibleCount = this.pageSize;
        },
        variantIcon(variant) {
            switch (variant) {
                case 'success': return 'bi bi-check-circle-fill text-success';
                case 'warning': return 'bi bi-exclamation-triangle-fill text-warning';
                case 'danger': return 'bi bi-x-circle-fill text-danger';
                case 'info':
                default:
                    return 'bi bi-info-circle-fill text-info';
            }
        },
        formatRelativeTime(ts) {
            if (!ts) return '';
            const diffSec = Math.max(0, Math.floor((this.nowTick - ts) / 1000));
            if (diffSec < 15) return 'just now';
            if (diffSec < 60) return `${diffSec}s ago`;
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `${diffMin}m ago`;
            const diffHr = Math.floor(diffMin / 60);
            if (diffHr < 24) return `${diffHr}h ago`;
            const diffDays = Math.floor(diffHr / 24);
            return `${diffDays}d ago`;
        },
        formatFullTime(ts) {
            if (!ts) return '';
            try {
                return new Date(ts).toLocaleString();
            } catch (_) {
                return '';
            }
        }
    }
};
</script>

<style scoped>
.vt-toast-history-root {
    position: fixed;
    bottom: 12px;
    right: 16px;
    z-index: 100040;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
}

.vt-toast-history-trigger,
.vt-toast-history-panel {
    pointer-events: auto;
}

.vt-toast-history-trigger {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 30px;
    padding: 0 10px;
    border-radius: 999px;
    border: 1px solid rgba(148, 163, 184, 0.35);
    background-color: rgba(255, 255, 255, 0.92);
    color: #334155;
    font-size: 0.76rem;
    font-weight: 600;
    cursor: pointer;
    backdrop-filter: blur(8px);
    transition: transform 0.14s ease, border-color 0.14s ease, background-color 0.14s ease, box-shadow 0.14s ease;
}

.vt-toast-history-trigger:hover,
.vt-toast-history-trigger.is-open {
    border-color: #0284c7;
    color: #0f172a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18) !important;
}

[data-theme="dark"] .vt-toast-history-trigger {
    background-color: rgba(26, 35, 50, 0.92);
    border-color: rgba(148, 163, 184, 0.28);
    color: #cbd5e1;
}

[data-theme="dark"] .vt-toast-history-trigger:hover,
[data-theme="dark"] .vt-toast-history-trigger.is-open {
    border-color: #38bdf8;
    color: #f8fafc;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5) !important;
}

.vt-toast-trigger-icon {
    font-size: 0.84rem;
    line-height: 1;
}

.vt-toast-trigger-count {
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
}

.vt-toast-trigger-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
}

.vt-toast-trigger-dot.dot-success {
    background-color: #10b981;
}
.vt-toast-trigger-dot.dot-info {
    background-color: #0ea5e9;
}
.vt-toast-trigger-dot.dot-warning {
    background-color: #f59e0b;
}
.vt-toast-trigger-dot.dot-danger {
    background-color: #ef4444;
}

.vt-toast-history-panel {
    margin-bottom: 8px;
    width: 360px;
    max-width: calc(100vw - 24px);
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.32);
    background-color: #ffffff;
    color: #1e293b;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

[data-theme="dark"] .vt-toast-history-panel {
    background-color: #161f2e;
    border-color: #2d3b4e;
    color: #e2e8f0;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6) !important;
}

.vt-toast-history-header {
    padding: 8px 12px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.22);
    background-color: rgba(248, 250, 252, 0.85);
}

[data-theme="dark"] .vt-toast-history-header {
    background-color: rgba(15, 23, 42, 0.65);
    border-bottom-color: rgba(148, 163, 184, 0.16);
}

.vt-toast-history-title {
    font-size: 0.8rem;
    line-height: 1.2;
}

.vt-toast-history-subtitle {
    font-size: 0.66rem;
    line-height: 1.15;
}

.vt-toast-history-total-pill {
    display: inline-block;
    padding: 1px 5px;
    font-size: 0.65rem;
    border-radius: 999px;
    background-color: rgba(2, 132, 199, 0.14);
    color: #0284c7;
}

[data-theme="dark"] .vt-toast-history-total-pill {
    background-color: rgba(56, 189, 248, 0.16);
    color: #38bdf8;
}

.vt-toast-clear-btn,
.vt-toast-close-btn {
    font-size: 0.72rem;
    text-decoration: none !important;
}

.vt-toast-clear-btn:hover,
.vt-toast-close-btn:hover {
    color: #ef4444 !important;
}

.vt-toast-history-list {
    max-height: 320px;
    overflow-y: auto;
    padding: 6px 8px;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.vt-toast-history-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-left-width: 3px;
    background-color: rgba(248, 250, 252, 0.7);
    font-size: 0.76rem;
    line-height: 1.3;
}

[data-theme="dark"] .vt-toast-history-item {
    background-color: rgba(30, 41, 59, 0.65);
    border-color: rgba(148, 163, 184, 0.16);
}

.vt-toast-item-success {
    border-left-color: #10b981 !important;
}
.vt-toast-item-info {
    border-left-color: #0ea5e9 !important;
}
.vt-toast-item-warning {
    border-left-color: #f59e0b !important;
}
.vt-toast-item-danger {
    border-left-color: #ef4444 !important;
}

.vt-toast-item-icon-col {
    font-size: 0.84rem;
    line-height: 1.2;
    margin-top: 1px;
    flex-shrink: 0;
}

.vt-toast-item-title {
    font-size: 0.76rem;
    word-break: break-word;
}

.vt-toast-repeat-badge {
    display: inline-block;
    padding: 0 4px;
    border-radius: 4px;
    font-size: 0.64rem;
    font-weight: 700;
    background-color: rgba(148, 163, 184, 0.22);
}

.vt-toast-item-time {
    font-size: 0.66rem;
    white-space: nowrap;
}

.vt-toast-item-msg {
    font-size: 0.72rem;
    color: #475569;
    word-break: break-word;
    white-space: pre-wrap;
}

[data-theme="dark"] .vt-toast-item-msg {
    color: #94a3b8;
}

.vt-toast-load-more-btn {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 5px;
}

.vt-toast-tray-fade-enter-active,
.vt-toast-tray-fade-leave-active {
    transition: opacity 0.14s ease, transform 0.14s ease;
}

.vt-toast-tray-fade-enter,
.vt-toast-tray-fade-leave-to {
    opacity: 0;
    transform: translateY(6px);
}
</style>
